import { IDKind } from '@startuptools/common/common';
import { z } from 'zod';
import { FieldType } from '@startuptools/db-schema/schema/agreements.types';
import { JuridicalKind } from '@startuptools/business-ui/identity/types';
export { FieldType } from '@startuptools/db-schema/schema/agreements.types';

export const textFieldContentSchema = z.string();
export const booleanFieldContentSchema = z.boolean();
export const dateFieldContentSchema = z.date();

export const templateFields = [
  {
    type: FieldType.DATE,
    label: 'Date',
  },
  {
    type: FieldType.VARIABLE_TEXT,
    label: 'Text',
  },
  {
    type: FieldType.BOOLEAN,
    label: 'Boolean',
  },
  {
    type: FieldType.IDENTITY,
    label: 'Identity',
  },
  {
    type: FieldType.MULTIPLE_CHOICE,
    label: 'Multiple choice',
    additionalFields: [
      {
        type: 'text',
        label: 'Option',
      },
    ],
  },
];

export const identityContentSchema = z.object({
  name: z.string(),
  id: z.string().uuid(),
  identityNumber: z.string(),
  identityNumberKind: z.nativeEnum(IDKind),
  juridicalKind: z.nativeEnum(JuridicalKind),
  address: z.string().nullable().optional(),
  countryCode: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  language: z.enum(['sv', 'en']),
});

export const identityFieldSchema = z.object({
  type: z.literal(FieldType.IDENTITY),
  name: z.string(),
  value: z.object({
    value: z.string().uuid().optional(),
    content: identityContentSchema.optional().nullable(),
  }),
  id: z.string().uuid(),
});

export const variableTextFieldSchema = z.object({
  type: z.literal(FieldType.VARIABLE_TEXT),
  name: z.string(),
  value: z.object({ value: z.string().optional().nullable(), content: z.string().optional().nullable() }),
  longForm: z.boolean().optional(),
  id: z.string().uuid(),
});

export const dateFieldSchema = z.object({
  type: z.literal(FieldType.DATE),
  name: z.string(),
  value: z.object({ value: z.date().optional().nullable(), content: z.date().optional().nullable() }),
  id: z.string().uuid(),
});

export const booleanFieldSchema = z.object({
  type: z.literal(FieldType.BOOLEAN),
  name: z.string(),
  value: z.object({ value: z.boolean().optional().nullable(), content: z.boolean().optional().nullable() }),
  id: z.string().uuid(),
  reverse: z.boolean().optional().nullable(),
});

export const multipleChoiceFieldSchema = z.object({
  type: z.literal(FieldType.MULTIPLE_CHOICE),
  name: z.string(),
  value: z.object({ value: z.string().optional().nullable(), content: z.string().optional().nullable() }),
  valueOptions: z.array(z.string()),
  id: z.string().uuid(),
});

export const fieldSchema = z.discriminatedUnion('type', [
  variableTextFieldSchema,
  dateFieldSchema,
  booleanFieldSchema,
  identityFieldSchema,
  multipleChoiceFieldSchema,
]);

export type VariableTextFieldContent = z.infer<typeof variableTextFieldSchema>['value']['content'];
export type DateFieldContent = z.infer<typeof dateFieldSchema>['value']['content'];
export type BooleanFieldContent = z.infer<typeof booleanFieldSchema>['value']['content'];
export type IdentityFieldContent = z.infer<typeof identityFieldSchema>['value']['content'];
export type MultipleChoiceFieldContent = z.infer<typeof multipleChoiceFieldSchema>['value']['content'];

export type FieldContent =
  | VariableTextFieldContent
  | DateFieldContent
  | BooleanFieldContent
  | IdentityFieldContent
  | MultipleChoiceFieldContent;

export type VariableTextField = z.infer<typeof variableTextFieldSchema>;
export type DateField = z.infer<typeof dateFieldSchema>;
export type BooleanField = z.infer<typeof booleanFieldSchema>;
export type IdentityField = z.infer<typeof identityFieldSchema>;
export type MultipleChoiceField = z.infer<typeof multipleChoiceFieldSchema>;
export type Field = z.infer<typeof fieldSchema>;

/* this is a duplicate of the CreateIdentityInput type in client-api, should probably
be generalized elsewhere.

Optional fields are both nullable and optional because GraphQL doesn't know the difference between
these.
*/
export const createIdentitySchema = z.object({
  id: z.string().uuid().optional().nullable(),
  identityNumberKind: z.nativeEnum(IDKind),
  identityNumber: z.string(),
  name: z.string(),
  address: z.string().optional().nullable(),
  countryCode: z.string().optional().nullable(),
  juridicalKind: z.nativeEnum(JuridicalKind),
  email: z.string().nullable().optional(),
  phone: z.string().optional().nullable(),
});

export const savefieldSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(FieldType.VARIABLE_TEXT),
    value: z.string(),
  }),
  z.object({
    type: z.literal(FieldType.DATE),
    value: z.date(),
  }),
  z.object({
    type: z.literal(FieldType.BOOLEAN),
    value: z.boolean(),
  }),
  z.object({
    type: z.literal(FieldType.IDENTITY),
    value: createIdentitySchema,
  }),
  z.object({
    type: z.literal(FieldType.MULTIPLE_CHOICE),
    value: z.string(),
  }),
]);

export type TemplateField = {
  id: string;
  name: string;
  type: FieldType;
};

export const quillInputSchema = z.object({
  id: z.string(),
  value: z.unknown(),
  name: z.string(),
  type: z.nativeEnum(FieldType),
  reverse: z.boolean().optional(),
});

export type QuillInput = z.infer<typeof quillInputSchema>;
